import { getImageFormatUrl, useBookitoSettings } from '@carlito/api';
import { LocalizedEventData } from '@carlito/bookito-client';
import { Trans } from 'react-i18next';
import { BookingForm } from '../BookitoForm/NewBookingForm';
import { isEventBookable } from '../BookitoForm/helper';
import { BookitoWidgetViewMode } from '../types';
import { useMemo } from 'react';

export type Props = {
  event: LocalizedEventData;
  date: Date;
  authId: string;
  availableOnDates: string[];
  isModalView?: boolean;
  viewMode?: BookitoWidgetViewMode;
};

function BookitoEventDetail({
  event,
  authId,
  availableOnDates,
  date,
  isModalView = false,
  viewMode,
}: Props) {
  const { description, bannerImg, title } = event;

  const { bookitoSettings } = useBookitoSettings({ hotelId: authId });
  const { isBookable } = useMemo(
    () =>
      isEventBookable({
        event,
        onDate: date,
        settings: bookitoSettings,
      }),
    [bookitoSettings, date, event]
  );

  let isBookableEvent = isBookable;

  if (isModalView) {
    isBookableEvent = true;
  }

  const shouldPreselectDateInCalendar = viewMode ? viewMode === 'Date' : undefined;

  const bannerImgUrl = getImageFormatUrl(bannerImg);

  return (
    <div className="bookito-event-detail relative px-6 py-2 md:flex">
      <div className="my-4 md:sticky top-10 text-slate-500 text-lg leading-relaxed w-full lg:w-1/2">
        {bannerImgUrl && (
          <img
            src={bannerImgUrl}
            alt="event display img"
            className="rounded-lg object-fill w-full h-auto"
          />
        )}
        <h2 className="text-light-contrast mt-4 font-extrabold not-italic text-2xl md:hidden">
          {title}
        </h2>
        {isBookableEvent && description && (
          <div className="text-light-contrast mt-7 lg:mt-14">
            <h3 className="non-italic text-base lg:text-xl font-bold">
              <Trans i18nKey="description"></Trans>
            </h3>
            <p className="bookito-event-detail__description my-4 text-sm lg:text-base leading-relaxed">
              {description}
            </p>
          </div>
        )}
      </div>
      <div className="my-4 text-lg leading-relaxed w-full md:mx-8 lg:w-1/2">
        <h2 className="bookito-event-detail__title text-light-contrast mt-2 font-extrabold not-italic text-4xl hidden md:block">
          {title}
        </h2>
        {isBookableEvent ? (
          <BookingForm
            event={event}
            date={date}
            authId={authId}
            availableOnDates={availableOnDates}
            shouldPreselectDateInCalendar={shouldPreselectDateInCalendar ?? !isModalView}
          />
        ) : (
          <p className="bookito-event-detail__description text-sm lg:text-base leading-relaxed ">
            {description}
          </p>
        )}
      </div>
    </div>
  );
}

export default BookitoEventDetail;
